import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star, Eye, Moon, Sparkles, Coins, Menu, X, LogOut, WifiOff, BookOpen, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Toaster } from 'sonner';
import PushNotificationToggle from './PushNotificationToggle';

const Layout = ({ children }) => {
  const { user, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const location = useLocation();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        // Comprobar actualizaciones cada 60 segundos
        setInterval(() => reg.update().catch(() => {}), 60000);
        // Cuando haya un SW nuevo instalado, activarlo y recargar
        reg.addEventListener('updatefound', () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              // Nuevo SW listo: recargar solo una vez cuando tome control
              let refreshed = false;
              navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (refreshed) return;
                refreshed = true;
                window.location.reload();
              });
            }
          });
        });
      }).catch(() => {});
    }
    
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);

  const navLinks = [
    { path: '/', label: 'Inicio', icon: Star },
    { path: '/tarot', label: 'Tarot', icon: Eye },
    { path: '/astrology', label: 'Astrologia', icon: Moon },
    { path: '/synastry', label: 'Sinastria', icon: Heart },
    { path: '/numerology', label: 'Numerologia', icon: Sparkles },
    { path: '/iching', label: 'I Ching', icon: BookOpen },
  ];

  return (
    <div className="mystic-bg min-h-screen">
      {/* Stars Background */}
      <div className="stars">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: Math.random() * 4 + 's',
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(11,14,26,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(230,195,106,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_mystic-cards-56/artifacts/wfhpmaw5_Madame-Blavatsky--Logo-2_20260216_193202_0000.png" 
              alt="Madame Blavatsky"
              className="w-10 h-10 rounded-full"
            />
            <span className="font-decorative text-lg text-gold-300" data-testid="app-logo">Madame Blavatsky</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`nav-link flex items-center gap-2 text-sm ${location.pathname === path ? 'active' : ''}`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </div>

          {/* User Section */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/credits" className="credit-badge" data-testid="credits-badge">
                  <Coins className="w-4 h-4" />
                  {user.credits}
                </Link>
                <div className="hidden md:flex items-center gap-2">
                  <PushNotificationToggle compact />
                  <Link to="/dashboard" className="flex items-center gap-2 nav-link" data-testid="nav-dashboard">
                    {user.picture && (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    )}
                    <span className="text-sm">{user.name?.split(' ')[0]}</span>
                  </Link>
                  <button onClick={logout} className="nav-link" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <Link to="/login" className="btn-gold text-sm" data-testid="nav-login">
                Entrar
              </Link>
            )}

            {/* Mobile Menu */}
            <button 
              className="md:hidden text-[var(--text-secondary)]"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/5 p-4 space-y-1" style={{ background: 'rgba(11,14,26,0.95)' }}>
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
                  location.pathname === path ? 'bg-purple-400/10 text-gold-300' : 'text-[var(--text-secondary)] hover:bg-white/5'
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            ))}
            {user && (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--text-secondary)] hover:bg-white/5">
                  Mi Perfil
                </Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--text-secondary)] hover:bg-white/5 w-full text-left">
                  <LogOut className="w-5 h-5" />
                  Cerrar Sesion
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-orange-600/90 backdrop-blur-sm text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="w-4 h-4" />
          Sin conexion - Mostrando contenido guardado
        </div>
      )}

      {/* Main Content */}
      <main className={`pt-16 relative z-10 ${isOffline ? 'mt-10' : ''}`}>
        {children}
      </main>

      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: 'rgba(20, 24, 47, 0.95)',
            border: '1px solid rgba(230, 195, 106, 0.15)',
            color: '#F5F5F7',
            fontFamily: 'Manrope, sans-serif',
          }
        }} 
      />
    </div>
  );
};

export default Layout;
